<script setup lang="ts">
const store = useSongStore();
const route = useRoute();
const songNumber = computed(() => route.params.songNumber as string);
const beta = useBetaStore();

const placeholderNotLoaded = computed(() => store.status !== LoadStatus.Ready);
const placeholderNotAuthorized = computed(
  () =>
    store.status === LoadStatus.Ready &&
    !store.song?.products.general.authorized,
);

const enableFullPlaybackFeature = computed(
  () => beta.features.fullSongPlayback,
);
</script>
<template>
  <div class="splash-wrapper">
    <SplashBackground
      v-if="store.bgSplashImgUrl"
      :img-url="store.bgSplashImgUrl"
    />
    <Container>
      <div class="flex">
        <div class="flex-1">
          <div class="flex items-center gap-2">
            <h1>
              <div v-if="placeholderNotLoaded">
                <span
                  class="dark placeholder"
                  style="width: 200px"
                />
              </div>
              <div v-else>{{ store.title }}</div>
            </h1>
            <div>
              <AddToFavorites
                v-if="!placeholderNotLoaded"
                class="add-to-fav"
                on-busy-bg
                :song-number="songNumber"
                :title="store.title"
              />
            </div>
          </div>

          <div class="meta-data">
            <div class="meta-data-group">
              <div
                v-if="!placeholderNotLoaded"
                class="meta-label"
              >
                {{ $t("S.LIT_Authors") }}
              </div>
              <div
                v-else
                class="meta-label"
              >
                <span
                  style="width: 80px"
                  class="dark placeholder"
                />
              </div>
              <div
                v-if="!placeholderNotLoaded"
                class="meta-value"
              >
                <router-link
                  v-for="(a, i) of store.song?.authors || []"
                  :key="i"
                  :to="`/search/results?${a.searchQuery}`"
                  class="authors link"
                  >{{ a.label }}</router-link
                >
              </div>
              <div
                v-else
                class="meta-value"
              >
                <span
                  v-for="i of 4"
                  :key="i"
                  class="link mr-1 text-lg"
                  ><span
                    class="dark placeholder"
                    style="width: 100px"
                /></span>
              </div>
            </div>
            <div
              v-if="placeholderNotAuthorized || placeholderNotLoaded"
              class="flex items-start gap-5 md:items-center"
            >
              <div class="meta-data-group">
                <div class="meta-label">
                  <span
                    class="dark placeholder"
                    style="width: 80px"
                  />
                </div>
                <div class="meta-value">
                  <span
                    class="dark placeholder"
                    style="width: 100px"
                  />
                </div>
              </div>
              <div class="meta-data-group">
                <div class="meta-label">
                  <span
                    class="dark placeholder"
                    style="width: 80px"
                  />
                </div>
                <div class="meta-value">
                  <span
                    class="dark placeholder"
                    style="width: 100px"
                  />
                </div>
              </div>
            </div>
            <div
              v-else
              class="flex items-start gap-5 md:items-center"
            >
              <div class="meta-data-group">
                <div class="meta-label">{{ $t("S.LIT_SongNumber") }}</div>
                <div class="meta-value">
                  <span>
                    {{ store.song?.ccliSongNumber }}
                  </span>
                  <CopyIcon
                    v-if="store.song?.ccliSongNumber"
                    id="songNumberCopyButton"
                    :value="store.song.ccliSongNumber"
                    data-user-activity="Copied Song Number"
                    busy-bg
                  />
                </div>
              </div>

              <div class="meta-data-group">
                <div class="meta-label">{{ $t("SV.LIT_DefaultKey") }}</div>
                <div class="meta-value">
                  {{ store.song?.defaultKey?.join(", ") }}
                </div>
                <div
                  v-if="!store.song?.defaultKey?.length"
                  class="meta-value"
                >
                  {{ $t("S.LIT_Unknown") }}
                </div>
              </div>

              <div
                v-if="store.song?.bpm"
                class="meta-data-group"
              >
                <div class="meta-label">{{ $t("SV.LIT_BPM") }}</div>
                <div class="meta-value">
                  {{ store.song?.bpm }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="store.data?.media && store.data.media.length"
        class="mediaplayer mt-1"
      >
        <MediaPlayerStickyFullSongPlayback v-if="enableFullPlaybackFeature" />
        <MediaPlayerSticky v-else />
      </div>
    </Container>
  </div>
</template>
<style scoped lang="postcss">
.splash-wrapper {
  @apply relative flex items-center justify-center bg-gray-800 py-2 text-white transition-all;
  min-height: 375px;
}
h1 {
  @apply text-4xl font-bold md:text-5xl;
}

.authors {
  @apply mr-1 inline;
  &:not(:last-of-type)::after {
    content: ",";
    @apply no-underline;
  }
  &:hover,
  &:focus-visible {
    @apply bg-transparent brightness-90;
  }
}
.meta-data {
  /*@apply flex items-center gap-x-5 gap-y-3 flex-wrap my-3;*/
  @apply my-2 grid gap-1;
}
.meta-data-group {
  @apply items-center gap-2 md:flex;
}
.meta-label {
  @apply text-sm opacity-75;
}
.meta-value {
  @apply text-lg;
}
.mediaplayer {
  :deep() {
    .album-artwork .content {
      @apply bg-white/10;
    }
  }
}
.splash-wrapper:deep() {
  button.copy-btn {
    i,
    svg {
      @apply text-white;
    }
  }
}

:deep() {
  .media-list {
    @apply pb-5;
  }
  .scrollbars {
    --scrollbar-fg: rgba(255, 255, 255, 0);
    &:hover,
    &.active {
      --scrollbar-fg: rgba(255, 255, 255, 0.25);
    }
  }
}
</style>
